import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useRefreshEffect } from "@/context/RefreshContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Archive, Trash2, Inbox, ExternalLink, Clock } from "lucide-react";
import FormDialog from "@/components/FormDialog";

const ARCHIVE_DELAY_MS = 1 * 60 * 1000;

const FIELDS = [
  { name: "aktenname", label: "Aktenname", type: "text", required: true },
  { name: "akten_link", label: "Verlinkung (Link)", type: "text", placeholder: "https://…" },
  { name: "officer", label: "Officer", type: "text" },
  { name: "department", label: "Department", type: "text" },
  { name: "bemerkung", label: "Bemerkung", type: "textarea", placeholder: "Beschreibung des Fehlers…" },
];

const makeDefault = (officer) => () => ({
  aktenname: "", akten_link: "", officer: officer || "", department: "", bemerkung: "",
});

function Countdown({ erledigtAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const end = new Date(erledigtAt).getTime() + ARCHIVE_DELAY_MS;
  const remaining = Math.max(0, end - now);
  const mm = Math.floor(remaining / 60000);
  const ss = Math.floor((remaining % 60000) / 1000);
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[11px] text-amber-400" title="Wird automatisch archiviert">
      <Clock className="h-3 w-3" /> {mm}:{String(ss).padStart(2, "0")}
    </span>
  );
}

export default function FehlerlisteSection({ officer }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const timersRef = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fehlerliste", { params: { archived: false } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useRefreshEffect(load);

  // Reload automatisch, sobald ein erledigter Eintrag die 5-Minuten-Marke erreicht.
  useEffect(() => {
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};
    rows.forEach((r) => {
      if (r.erledigt && r.erledigt_at) {
        const end = new Date(r.erledigt_at).getTime() + ARCHIVE_DELAY_MS;
        const delay = end - Date.now();
        timersRef.current[r.id] = setTimeout(load, Math.max(1000, delay + 500));
      }
    });
    return () => Object.values(timersRef.current).forEach(clearTimeout);
  }, [rows, load]);

  const openCreate = () => { setEditing(null); setValues(makeDefault(officer)()); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setValues({ ...row }); setDialogOpen(true); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = FIELDS.reduce((a, f) => ({ ...a, [f.name]: values[f.name] ?? "" }), {});
      if (editing) {
        await api.put(`/fehlerliste/${editing.id}`, payload);
        toast.success("Eintrag aktualisiert");
      } else {
        await api.post("/fehlerliste", payload);
        toast.success("Eintrag erstellt");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async (row, field) => {
    const next = !row[field];
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, [field]: next, erledigt_at: field === "erledigt" ? (next ? new Date().toISOString() : null) : r.erledigt_at } : r)));
    try {
      await api.patch(`/fehlerliste/${row.id}/status`, { [field]: next });
      if (field === "erledigt" && next) toast.info("Wird in 1 Minute archiviert – zum Abbrechen Haken entfernen");
    } catch (e) {
      toast.error("Aktion fehlgeschlagen");
      load();
    }
  };

  const doAction = async () => {
    if (!confirm) return;
    const { type, row } = confirm;
    try {
      if (type === "delete") {
        await api.delete(`/fehlerliste/${row.id}`);
        toast.success("Eintrag gelöscht");
      } else {
        await api.patch(`/fehlerliste/${row.id}/archive`, null, { params: { archived: true } });
        toast.success("Archiviert");
      }
      load();
    } catch (e) {
      toast.error("Aktion fehlgeschlagen");
    } finally {
      setConfirm(null);
    }
  };

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return ["aktenname", "officer", "department", "bemerkung"].some((k) => String(r[k] ?? "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            data-testid="fehler-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen…"
            className="pl-9 bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary"
          />
        </div>
        <Button
          data-testid="fehler-create-btn"
          onClick={openCreate}
          className="ml-auto rounded-sm bg-primary hover:bg-blue-600 text-white gap-2"
        >
          <Plus className="h-4 w-4" /> Fehler erfassen
        </Button>
      </div>

      <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              {["Aktenname", "Officer", "Department", "Bemerkung"].map((h) => (
                <TableHead key={h} className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 h-11">{h}</TableHead>
              ))}
              <TableHead className="text-center text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 w-[80px]">Gelesen</TableHead>
              <TableHead className="text-center text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 w-[130px]">Erledigt</TableHead>
              <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 w-[120px]">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={7} className="text-center py-10 text-zinc-500 font-mono text-sm">Wird geladen…</TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={7} className="text-center py-14">
                  <div className="flex flex-col items-center gap-2 text-zinc-600">
                    <Inbox className="h-8 w-8" />
                    <span className="font-mono text-sm">Keine Fehler erfasst</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => (
                <TableRow key={row.id} data-testid="fehler-row" className="border-zinc-800 hover:bg-zinc-900/50 transition-colors duration-150">
                  <TableCell className="text-sm align-top">
                    {row.akten_link && /^https?:\/\//i.test(row.akten_link) ? (
                      <a href={row.akten_link} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 font-medium">
                        {row.aktenname || "Akte"} <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-zinc-200 font-medium">{row.aktenname || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-zinc-300 align-top">{row.officer || <span className="text-zinc-600">—</span>}</TableCell>
                  <TableCell className="text-sm text-zinc-300 align-top">{row.department || <span className="text-zinc-600">—</span>}</TableCell>
                  <TableCell className="text-sm text-zinc-300 align-top"><span className="block max-w-md whitespace-pre-wrap">{row.bemerkung || "—"}</span></TableCell>
                  <TableCell className="text-center align-top">
                    <input
                      type="checkbox"
                      data-testid="fehler-gelesen"
                      checked={!!row.gelesen}
                      onChange={() => toggleStatus(row, "gelesen")}
                      className="h-4 w-4 accent-primary cursor-pointer"
                    />
                  </TableCell>
                  <TableCell className="text-center align-top">
                    <div className="flex flex-col items-center gap-1">
                      <input
                        type="checkbox"
                        data-testid="fehler-erledigt"
                        checked={!!row.erledigt}
                        onChange={() => toggleStatus(row, "erledigt")}
                        className="h-4 w-4 accent-emerald-500 cursor-pointer"
                      />
                      {row.erledigt && row.erledigt_at && <Countdown erledigtAt={row.erledigt_at} />}
                    </div>
                  </TableCell>
                  <TableCell className="text-right align-top">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid="fehler-edit" size="icon" variant="ghost" onClick={() => openEdit(row)} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-primary hover:bg-primary/10" title="Bearbeiten"><Pencil className="h-4 w-4" /></Button>
                      <Button data-testid="fehler-archive" size="icon" variant="ghost" onClick={() => setConfirm({ type: "archive", row })} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10" title="Archivieren"><Archive className="h-4 w-4" /></Button>
                      <Button data-testid="fehler-delete" size="icon" variant="ghost" onClick={() => setConfirm({ type: "delete", row })} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10" title="Löschen"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <FormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editing ? "Fehler bearbeiten" : "Fehler erfassen"}
        fields={FIELDS}
        values={values}
        setValues={setValues}
        onSubmit={submit}
        submitting={submitting}
        testidPrefix="fehler"
      />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zinc-100">{confirm?.type === "delete" ? "Eintrag löschen?" : "Eintrag archivieren?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">{confirm?.type === "delete" ? "Dieser Vorgang kann nicht rückgängig gemacht werden." : "Der Eintrag wird in das Archiv verschoben."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm bg-transparent border-zinc-700 text-zinc-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="fehler-confirm" onClick={doAction} className={`rounded-sm ${confirm?.type === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-blue-600"} text-white`}>Bestätigen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
